import { get, post } from './api'

export interface AuthSession {
  user: { id: string; email?: string; role?: string }
  access_token: string
}

export interface AuthResult {
  data: { session: AuthSession } | null
  error: Error | null
}

export interface Profile {
  id: string
  username?: string
  email?: string
  phone?: string
  auth_provider?: 'phone' | 'email' | 'wx_open'
  verified_at?: string
  nickname?: string
  avatar_url?: string
  points: number
  is_admin: boolean
  role: 'user' | 'admin'
  is_premium?: boolean
  membership_type?: 'day' | 'month' | 'year' | null
  membership_expires_at?: string
  created_at: string
  updated_at: string
}

function tokenFromStorage(): string | null {
  return localStorage.getItem('ppt_token')
}
function storeToken(token: string | null) {
  if (token) localStorage.setItem('ppt_token', token)
  else localStorage.removeItem('ppt_token')
}

export async function getSession(): Promise<AuthSession | null> {
  const token = tokenFromStorage()
  if (!token) return null
  try {
    const data = await get<{ user: { id: string; email?: string; role?: string } }>('/auth/me')
    return { user: data.user, access_token: token }
  }
  catch {
    return null
  }
}

export async function signIn(email: string, password: string): Promise<AuthResult> {
  try {
    const data = await post<{ access_token: string; user: { id: string; email?: string; role?: string } }>(
      '/auth/login',
      { email, password },
    )
    storeToken(data.access_token)
    return { data: { session: { user: data.user, access_token: data.access_token } }, error: null }
  }
  catch (e) {
    return { data: null, error: e instanceof Error ? e : new Error('登录失败') }
  }
}

export async function register(email: string, password: string): Promise<AuthResult> {
  try {
    await post<{ user: { id: string; email?: string; role?: string } }>('/auth/register', { email, password })
    // After register, sign in to get token
    return signIn(email, password)
  }
  catch (e) {
    return { data: null, error: e instanceof Error ? e : new Error('注册失败') }
  }
}

export function signOut(): void {
  storeToken(null)
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await post('/auth/change-password', { oldPassword, newPassword })
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const session = await getSession()
  if (!session) return null
  const u = session.user as Record<string, unknown>
  const role = u.role === 'admin' ? 'admin' : 'user'
  return {
    id: u.id as string,
    email: u.email as string | undefined,
    points: (u.points_balance as number) ?? 0,
    is_admin: role === 'admin',
    role,
    created_at: (u.created_at as string) || '',
    updated_at: (u.updated_at as string) || '',
  } as Profile
}
